import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import NavBar from "../components/NavBar";
import Loading from "../components/Loading";
import Highlighter from "../components/Highlighter";
import api from "../api";

const UnsubmittedLeadsPage = () => {
  const [unsubmittedLeads, setUnsubmittedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("lastActiveAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [minCompletion, setMinCompletion] = useState(10);
  const [selectedLead, setSelectedLead] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    highCompletion: 0,
    recentActivity: 0,
  });

  const pageSize = 10;

  // Fetch unsubmitted leads
  const fetchUnsubmittedLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder,
        search: searchTerm,
        minCompletion,
      };

      const response = await api.getUnsubmittedLeads(params);
      setUnsubmittedLeads(response.data.unsubmittedLeads);
      setTotalPages(response.data.pagination.totalPages);
      setTotalRecords(response.data.pagination.totalRecords);
    } catch (error) {
      toast.error("Failed to fetch unsubmitted leads: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, sortBy, sortOrder, minCompletion]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.getUnsubmittedLeadsStats();
      setStats(response.data);
    } catch (error) {
      // Failed to fetch stats
    }
  }, []);

  useEffect(() => {
    fetchUnsubmittedLeads();
  }, [fetchUnsubmittedLeads]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this unsubmitted lead?")
    ) {
      return;
    }

    try {
      await api.deleteUnsubmittedLead(id);
      toast.success("Unsubmitted lead deleted successfully");
      fetchUnsubmittedLeads();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete unsubmitted lead: " + error.message);
    }
  };

  // Handle view details
  const handleViewDetails = async (id) => {
    try {
      const response = await api.getUnsubmittedLeadById(id);
      setSelectedLead(response.data);
    } catch (error) {
      toast.error("Failed to fetch lead details: " + error.message);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Calculate completion color
  const getCompletionBadge = (percentage) => {
    let badgeClass = "px-2 py-1 rounded-full text-xs font-medium ";
    if (percentage >= 75) badgeClass += "bg-green-100 text-green-800";
    else if (percentage >= 50) badgeClass += "bg-yellow-100 text-yellow-800";
    else if (percentage >= 25) badgeClass += "bg-orange-100 text-orange-800";
    else badgeClass += "bg-red-100 text-red-800";

    return <span className={badgeClass}>{percentage}%</span>;
  };

  if (loading && currentPage === 1) {
    return <Loading />;
  }

  return (
    <>
      <NavBar currentPage="/unsubmitted-leads" />
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Unsubmitted Leads
            </h1>
            <p className="text-gray-600">
              Track and follow up on incomplete form submissions to maximize
              conversion opportunities.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Total Unsubmitted
              </h3>
              <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                High Completion (50%+)
              </h3>
              <p className="text-3xl font-bold text-green-600">
                {stats.highCompletion}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Recent Activity (24h)
              </h3>
              <p className="text-3xl font-bold text-orange-600">
                {stats.recentActivity}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white p-6 rounded-lg shadow-sm border mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  placeholder="Search by name, email, company..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Completion %
                </label>
                <select
                  value={minCompletion}
                  onChange={(e) => {
                    setMinCompletion(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>All (0%+)</option>
                  <option value={10}>Basic (10%+)</option>
                  <option value={25}>Engaged (25%+)</option>
                  <option value={50}>Committed (50%+)</option>
                  <option value={75}>Nearly Complete (75%+)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="lastActiveAt">Last Active</option>
                  <option value="createdAt">Created Date</option>
                  <option value="completionPercentage">Completion %</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white rounded-lg shadow-sm border">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : unsubmittedLeads.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No unsubmitted leads found.</p>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      {totalRecords} Unsubmitted Lead
                      {totalRecords !== 1 ? "s" : ""}
                    </h3>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          onClick={() => handleSortChange("name")}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          Name{" "}
                          {sortBy === "name" &&
                            (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          onClick={() => handleSortChange("email")}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          Email{" "}
                          {sortBy === "email" &&
                            (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th
                          onClick={() =>
                            handleSortChange("completionPercentage")
                          }
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          Completion{" "}
                          {sortBy === "completionPercentage" &&
                            (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          onClick={() => handleSortChange("lastActiveAt")}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          Last Active{" "}
                          {sortBy === "lastActiveAt" &&
                            (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {unsubmittedLeads.map((lead) => (
                        <tr key={lead._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              <Highlighter
                                text={lead.name || "N/A"}
                                highlight={searchTerm}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <Highlighter
                                text={lead.email || "N/A"}
                                highlight={searchTerm}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <Highlighter
                                text={lead.company || "N/A"}
                                highlight={searchTerm}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getCompletionBadge(lead.completionPercentage)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(lead.lastActiveAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleViewDetails(lead._id)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDelete(lead._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                        {Math.min(currentPage * pageSize, totalRecords)} of{" "}
                        {totalRecords} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            const page = i + 1;
                            return (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`px-3 py-1 text-sm border rounded-md ${
                                  currentPage === page
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                {page}
                              </button>
                            );
                          }
                        )}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Details Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Unsubmitted Lead Details
                  </h2>
                  <button
                    onClick={() => setSelectedLead(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Basic Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Name
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedLead.name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedLead.email || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Company
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedLead.company || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Phone
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedLead.phone || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Progress Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Completion
                        </label>
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{
                                width: `${selectedLead.completionPercentage}%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedLead.completionPercentage}%
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Current Step
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedLead.currentStep || 0}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Created
                        </label>
                        <p className="text-sm text-gray-900">
                          {formatDate(selectedLead.createdAt)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Last Active
                        </label>
                        <p className="text-sm text-gray-900">
                          {formatDate(selectedLead.lastActiveAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Data */}
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Form Data
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(selectedLead)
                        .filter(
                          ([key, value]) =>
                            ![
                              "_id",
                              "__v",
                              "sessionId",
                              "userAgent",
                              "ipAddress",
                              "createdAt",
                              "updatedAt",
                              "lastActiveAt",
                              "completionPercentage",
                              "currentStep",
                              "isConverted",
                              "convertedLeadId",
                            ].includes(key) &&
                            value !== null &&
                            value !== undefined &&
                            value !== ""
                        )
                        .map(([key, value]) => (
                          <div
                            key={key}
                            className="border-b border-gray-200 pb-2"
                          >
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {key.replace(/([A-Z])/g, " $1").trim()}
                            </label>
                            <p className="text-sm text-gray-900 mt-1">
                              {typeof value === "boolean"
                                ? value
                                  ? "Yes"
                                  : "No"
                                : value.toString()}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UnsubmittedLeadsPage;
